import { useMemo, useState } from "react";
import { Link, useParams } from "react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useAccount } from "wagmi";

import { PaymentSheet } from "@/components/PaymentSheet";
import {
  ASQAmount,
  EmptyState,
  ErrorState,
  Panel,
  RegistryBlock,
  SheetLabel,
  Skeleton,
  Status,
} from "@/components/primitives";
import { api, type Product } from "@/lib/api";
import { formatASQ } from "@/lib/format";
import { usePayment } from "@/lib/use-payment";

/**
 * Catalog — plan §22, §37A.
 *
 * The metaphor is an architectural material catalogue: a category index, a
 * dominant first entry, and a matrix of smaller ones beneath (§62). Product
 * imagery is not in the repository, so each entry gets a generated plate
 * instead — see ProductPlate for why that is a deliberate choice rather than a
 * missing asset.
 */
export function CatalogPage() {
  const [category, setCategory] = useState("Semua");

  const { data, isPending, isError } = useQuery({
    queryKey: ["catalog"],
    queryFn: api.catalog,
  });

  const filtered = useMemo(() => {
    const products = data?.products ?? [];
    return category === "Semua"
      ? products
      : products.filter((product) => product.category === category);
  }, [data, category]);

  const [lead, ...rest] = filtered;

  return (
    <div className="sheet py-16">
      <SheetLabel coordinate="ASETQU_CATALOG">Material catalogue</SheetLabel>
      <h1 className="display-section mt-5 text-paper-50">Catalog</h1>
      <p className="mt-5 max-w-xl text-base text-paper-300/70">
        Produk dan jasa ekosistem properti, dibayar penuh dengan ASQ. Pembayaran
        masuk ke Catalog Permanent Vault — di lingkungan pengujian ini tidak ada
        settlement ke penjual.
      </p>

      {data?.unavailable ? (
        <div className="mt-10">
          <ErrorState title="Catalog tidak tersedia" code={data.unavailable.code}>
            {data.unavailable.detail}
          </ErrorState>
        </div>
      ) : null}

      {/* Category index — a horizontal rule of labels, like a drawing index. */}
      <nav aria-label="Kategori" className="mt-12 border-y border-ink-700">
        <ul className="flex flex-wrap">
          {["Semua", ...(data?.categories ?? [])].map((entry) => (
            <li key={entry}>
              <button
                type="button"
                onClick={() => setCategory(entry)}
                aria-pressed={category === entry}
                className={`micro border-r border-ink-700 px-5 py-4 transition-colors ${
                  category === entry ? "text-hub-700" : "hover:text-paper-50"
                }`}
              >
                {entry}
              </button>
            </li>
          ))}
        </ul>
      </nav>

      {isPending ? (
        <div className="mt-12 space-y-6">
          <Skeleton className="h-80 w-full" />
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            <Skeleton className="h-64" />
            <Skeleton className="h-64" />
            <Skeleton className="h-64" />
          </div>
        </div>
      ) : isError ? (
        <div className="mt-12">
          <ErrorState title="Tidak dapat memuat katalog" code="LOAD_FAILED">
            Coba muat ulang halaman.
          </ErrorState>
        </div>
      ) : filtered.length === 0 ? (
        <div className="mt-12">
          <EmptyState title="Tidak ada produk di kategori ini">
            Pilih kategori lain untuk melihat produk yang tersedia.
          </EmptyState>
        </div>
      ) : (
        <div className="mt-12 space-y-6">
          {lead ? <LeadProduct product={lead} /> : null}
          {rest.length ? (
            <ul className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {rest.map((product) => (
                <li key={product.id}>
                  <ProductTile product={product} />
                </li>
              ))}
            </ul>
          ) : null}
        </div>
      )}
    </div>
  );
}

/**
 * A generated plate standing in for product photography.
 *
 * Plan §53 asks for real editorial imagery and none was supplied with the
 * brief. Rather than ship a grey box with a camera icon — the placeholder look
 * §53 explicitly rejects — each product gets a deterministic technical plate
 * derived from its own id: a quiet field with a plan-view figure, in the
 * technical vernacular the rest of the app already uses.
 *
 * It reads as an intentional illustration rather than a missing file, and it is
 * a single component to replace when real photography arrives.
 */
function ProductPlate({ product, tall = false }: { product: Product; tall?: boolean }) {
  // Deterministic from the slug, so a product's plate never changes between
  // renders or between machines.
  const seed = [...product.slug].reduce((sum, character) => sum + character.charCodeAt(0), 0);
  const rotation = seed % 4;
  const bars = 3 + (seed % 3);

  return (
    <div
      className={`relative overflow-hidden border-b border-ink-700 bg-ink-850 ${
        tall ? "aspect-[4/5]" : "aspect-square"
      }`}
      aria-hidden="true"
    >
      <div className="absolute inset-x-0 bottom-0 h-[38%] bg-hub-500/10" />
      <div
        className="absolute inset-[18%] border border-ink-600"
        style={{ transform: `rotate(${rotation * 3 - 4.5}deg)` }}
      >
        <div className="flex h-full flex-col justify-end gap-1.5 p-4">
          {Array.from({ length: bars }, (_, index) => (
            <span
              key={index}
              className="block h-px bg-hub-300/40"
              style={{ width: `${30 + ((seed + index * 17) % 60)}%` }}
            />
          ))}
        </div>
      </div>
      <span className="absolute left-4 top-4 micro">{product.category}</span>
    </div>
  );
}

function LeadProduct({ product }: { product: Product }) {
  return (
    <Panel marked className="grid overflow-hidden lg:grid-cols-2">
      <ProductPlate product={product} tall />
      <div className="flex flex-col justify-between gap-8 p-8 lg:p-12">
        <div>
          <SheetLabel>{product.provider}</SheetLabel>
          <h2 className="display-section mt-4 text-paper-50">{product.name}</h2>
          <p className="mt-5 max-w-md text-sm text-paper-300/70">{product.description}</p>
        </div>

        <div>
          <div className="flex items-end justify-between gap-6 border-t border-ink-700 pt-6">
            <ASQAmount raw={product.priceRaw} size="xl" />
            <p className="micro">Stok {product.stock}</p>
          </div>
          <Link to={`/catalog/${product.slug}`} className="btn btn--primary mt-6 w-full sm:w-auto">
            Lihat Produk
          </Link>
        </div>
      </div>
    </Panel>
  );
}

function ProductTile({ product }: { product: Product }) {
  return (
    <Link
      to={`/catalog/${product.slug}`}
      className="group block h-full focus-visible:outline-2 focus-visible:outline-hub-300"
    >
      <Panel className="flex h-full flex-col overflow-hidden">
        <ProductPlate product={product} />
        <div className="flex flex-1 flex-col justify-between gap-5 p-5">
          <div>
            <h3 className="display-section text-base text-paper-50 group-hover:text-hub-300">
              {product.name}
            </h3>
            <p className="micro mt-2">{product.provider}</p>
          </div>
          <div className="flex items-end justify-between gap-3">
            <ASQAmount raw={product.priceRaw} size="md" />
            <p className="micro">Stok {product.stock}</p>
          </div>
        </div>
      </Panel>
    </Link>
  );
}

/* -------------------------------------------------------------------------- */
/* Product detail                                                              */
/* -------------------------------------------------------------------------- */

export function ProductPage() {
  const { slug } = useParams();
  const { address, isConnected } = useAccount();
  const queryClient = useQueryClient();
  const [sheetOpen, setSheetOpen] = useState(false);
  const { state, pay, reset } = usePayment();

  const { data: product, isPending, isError } = useQuery({
    queryKey: ["product", slug],
    queryFn: () => api.product(slug!),
    enabled: Boolean(slug),
  });

  if (isPending) {
    return (
      <div className="sheet space-y-6 py-16">
        <Skeleton className="h-10 w-64" />
        <Skeleton className="h-96 w-full" />
      </div>
    );
  }

  if (isError || !product) {
    return (
      <div className="sheet py-16">
        <ErrorState title="Produk tidak ditemukan" code="NOT_FOUND">
          Produk ini mungkin sudah tidak aktif.{" "}
          <Link to="/catalog" className="linkline text-hub-300">
            Kembali ke katalog
          </Link>
          .
        </ErrorState>
      </div>
    );
  }

  const utility = product.utility;
  const soldOut = product.stock <= 0;

  const closeSheet = () => {
    if (state.step === "done") {
      queryClient.invalidateQueries({ queryKey: ["product", slug] });
      queryClient.invalidateQueries({ queryKey: ["catalog"] });
      queryClient.invalidateQueries({ queryKey: ["orders", address] });
      queryClient.invalidateQueries({ queryKey: ["utilities"] });
    }
    setSheetOpen(false);
    reset();
  };

  return (
    <div className="sheet py-16">
      <Link to="/catalog" className="micro linkline">
        ← Catalog
      </Link>

      <div className="mt-8 grid gap-12 lg:grid-cols-12">
        <div className="lg:col-span-7">
          <Panel className="overflow-hidden">
            <ProductPlate product={product} tall />
          </Panel>
        </div>

        <div className="lg:col-span-5">
          <SheetLabel>{product.category}</SheetLabel>
          <h1 className="display-section mt-4 text-paper-50">{product.name}</h1>

          <p className="mt-6">
            <ASQAmount raw={product.priceRaw} size="xl" />
          </p>

          <p className="mt-6 text-sm text-paper-300/70">{product.description}</p>

          <dl className="mt-8 divide-y divide-ink-700 border-y border-ink-700">
            <div className="flex items-baseline justify-between gap-4 py-3">
              <dt className="micro">Provider</dt>
              <dd className="text-sm text-paper-100">{product.provider}</dd>
            </div>
            <div className="flex items-baseline justify-between gap-4 py-3">
              <dt className="micro">Stok</dt>
              <dd className="text-sm text-paper-100">
                {soldOut ? <Status state="bad">Habis</Status> : product.stock}
              </dd>
            </div>
          </dl>

          {utility ? (
            <div className="mt-8">
              <RegistryBlock utility={utility} />
            </div>
          ) : null}

          <button
            type="button"
            className="btn btn--primary mt-8 w-full"
            onClick={() => setSheetOpen(true)}
            disabled={!isConnected || soldOut || !utility?.usable}
          >
            {soldOut ? "Stok Habis" : `Beli dengan ${formatASQ(product.priceRaw)} ASQ`}
          </button>
          {!isConnected ? (
            <p className="micro mt-3 text-center text-warn">
              Hubungkan wallet untuk membeli
            </p>
          ) : null}
        </div>
      </div>

      {utility ? (
        <PaymentSheet
          open={sheetOpen}
          onClose={closeSheet}
          title="Beli Produk"
          subject={product.name}
          details={[
            { label: "Provider", value: product.provider },
            { label: "Kategori", value: product.category },
            { label: "Jumlah", value: "1" },
          ]}
          amountRaw={product.priceRaw}
          utility={utility}
          state={state}
          onConfirm={() => pay({ utility: "ASETQU_CATALOG", productId: product.id })}
        />
      ) : null}
    </div>
  );
}
